import { useState, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';

export type DataFileName =
  | '_meta' | 'business' | 'content' | 'theme'
  | 'services' | 'areas' | 'testimonials' | 'faqs' | 'posts';

interface UseWebsiteDataFileResult<T = any> {
  data: T | undefined;
  sha: string | undefined;
  isLoading: boolean;
  error: string | null;
  save: (newData: T) => Promise<{ success: boolean; error?: string }>;
  isSaving: boolean;
  reload: () => void;
}

export function useWebsiteDataFile<T = any>(
  websiteId: string | undefined,
  file: DataFileName
): UseWebsiteDataFileResult<T> {
  const queryClient = useQueryClient();
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const queryKey = ['website-content', websiteId, file];

  const { data: result, isLoading, error: fetchError } = useQuery({
    queryKey,
    queryFn: async () => {
      const res = await api.get(`/api/websites/${websiteId}/content/${file}`);
      return res.data as { data: T; sha: string };
    },
    enabled: !!websiteId,
    staleTime: 30_000,
  });

  const save = useCallback(async (newData: T) => {
    if (!websiteId || !result?.sha) {
      return { success: false, error: 'No data loaded yet' };
    }

    setIsSaving(true);
    setSaveError(null);

    try {
      const res = await api.put(`/api/websites/${websiteId}/content/${file}`, {
        data: newData,
        sha: result.sha,
      });

      if (res.data.success) {
        // Invalidate to refetch with new sha
        queryClient.invalidateQueries({ queryKey });
        return { success: true };
      }
      return { success: false, error: 'Unexpected response' };
    } catch (err: any) {
      const msg = err.response?.data?.error || err.message || 'Save failed';
      const issues = err.response?.data?.issues;
      const errorMsg = issues
        ? `Validation: ${issues.map((i: any) => `${i.path}: ${i.message}`).join('; ')}`
        : msg;
      setSaveError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setIsSaving(false);
    }
  }, [websiteId, file, result?.sha, queryClient]);

  const reload = useCallback(() => {
    queryClient.invalidateQueries({ queryKey });
  }, [queryClient, queryKey]);

  return {
    data: result?.data,
    sha: result?.sha,
    isLoading,
    error: saveError || (fetchError ? String(fetchError) : null),
    save,
    isSaving,
    reload,
  };
}
